import { formatShelfTagPrice, shelfTagOptions } from './shelfTags.mjs';

const pdfText = value => String(value ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[–—]/g, '-').replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/[^\x20-\x7e]/g, '').trim();

export async function createShelfTagPdf(options) {
  const { errors, size, copies, price, barcode } = shelfTagOptions(options);
  if (Object.keys(errors).length) throw new Error(Object.values(errors)[0]);
  const { jsPDF } = await import('jspdf');
  const width = size.width * 72, height = size.height * 72;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: [width, height], compress: true, putOnlyUsedFonts: true });
  pdf.setProperties({ title: `Shelf Tags - ${pdfText(options.product.itemNumber)}`, subject: `${size.width} x ${size.height} inch shelf labels`, creator: 'The Shooting Warehouse' });
  const margin = 7.2;
  const contentWidth = width - margin * 2;
  const headerSize = Math.min(14, height * 0.135);
  const titleSize = Math.min(11, height * 0.112);
  const upcSize = Math.min(11, height * 0.115);
  const fitText = (text, maxWidth, fontSize, minSize = 6) => {
    let size = fontSize;
    pdf.setFontSize(size);
    while (pdf.getTextWidth(text) > maxWidth && size > minSize) { size = Math.max(minSize, size - 0.25); pdf.setFontSize(size); }
    while (pdf.getTextWidth(text) > maxWidth && text.length) text = text.slice(0, -1);
    return { text, size };
  };
  for (let copy = 0; copy < copies; copy++) {
    if (copy) pdf.addPage([width, height], 'landscape');
    pdf.setTextColor(0); pdf.setFillColor(0);
    pdf.setFont('helvetica', 'bold');
    const priceText = formatShelfTagPrice(price);
    const header = fitText(priceText, contentWidth * 0.59, headerSize);
    pdf.text(header.text, width - margin, margin + headerSize * 0.8, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    const item = fitText(pdfText(options.product.itemNumber), contentWidth * 0.38, headerSize);
    pdf.text(item.text, margin, margin + headerSize * 0.8);
    pdf.setFontSize(titleSize);
    let titleLines = pdf.splitTextToSize(pdfText(options.product.name), contentWidth);
    if (titleLines.length > 2) {
      const lastLine = titleLines[1];
      titleLines = [titleLines[0], fitText(`${lastLine.slice(0, -3)}...`, contentWidth, titleSize).text];
    }
    pdf.setFontSize(titleSize);
    const titleTop = margin + headerSize + titleSize;
    pdf.text(titleLines, width / 2, titleTop, { align: 'center', lineHeightFactor: 1.05 });
    const barTop = titleTop + (titleLines.length - 1) * titleSize * 1.05 + 5;
    const barBottom = height - margin - upcSize - 4;
    // Ten modules of white space on either side are part of the barcode.
    const moduleWidth = contentWidth / (barcode.modules.length + 20);
    const left = margin + moduleWidth * 10;
    for (let i = 0; i < barcode.modules.length; i++) {
      if (barcode.modules[i] !== '1') continue;
      const start = i;
      while (barcode.modules[i + 1] === '1') i++;
      pdf.rect(left + start * moduleWidth, barTop, (i - start + 1) * moduleWidth, barBottom - barTop, 'F');
    }
    pdf.setFontSize(upcSize);
    pdf.text(barcode.value, width / 2, height - margin, { align: 'center' });
  }
  return pdf.output('blob');
}

export function navigateToShelfTagPdf(blob, location = window.location, urls = URL) {
  const url = urls.createObjectURL(blob);
  try {
    // The PHP button opens this route; display the PDF in that same tab.
    // No second popup or user gesture is needed after loading the product.
    location.replace(url);
  } catch (error) {
    urls.revokeObjectURL(url);
    throw error;
  }
}
